import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import { formatUSADate, formatUSADateTime, formatNaiveDateTime } from "../../../utils/dateFormatters";
import { SYMPTOM_STATES, parseSymptomsString, wasCreatedOnOrAfterSymptomsCutoff } from "../../../utils/symptomTriState";
import { getHeightUnit } from "../../../utils/heightUnit";
import { parseBoldSegments } from "../../../utils/parseBoldMarkers";

const styles = StyleSheet.create({
  page: {
    padding: 40,
    paddingBottom: 60,
    fontSize: 10,
    fontFamily: "Helvetica",
    backgroundColor: "#ffffff",
    color: "#333333",
    position: "relative"
  },
  watermark: {
    position: "absolute",
    top: "35%",
    left: "10%",
    width: "80%",
    opacity: 0.04,
  },
  header: {
    marginBottom: 20
  },
  logo: {
    width: 130,
    height: 30,
    marginBottom: 5,
    objectFit: "contain"
  },
  headerSeparator: {
    width: 140,
    height: 2,
    backgroundColor: "#333333",
    marginBottom: 10
  },
  locationName: {
    fontSize: 11,
    color: "#888888",
    marginBottom: 5
  },
  reportTitleRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
    paddingBottom: 8,
    marginBottom: 10
  },
  reportTitle: {
    fontSize: 16,
    fontFamily: "Helvetica-Bold",
    color: "#222222"
  },
  reportDate: {
    fontSize: 11,
    color: "#444444"
  },
  lastEdited: {
    fontSize: 9,
    color: "#888",
    fontStyle: 'italic',
  },
  section: {
    marginBottom: 20,
  },
  sectionTitleWrapper: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 10
  },
  blueBar: {
    width: 3,
    height: 15,
    backgroundColor: "#4a90e2",
    marginRight: 8
  },
  sectionTitle: {
    fontSize: 14,
    fontFamily: "Helvetica-Bold",
    color: "#333333"
  },
  subSectionTitle: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    marginTop: 10,
    marginBottom: 5,
    borderBottomWidth: 0.5,
    borderBottomColor: "#eeeeee",
    paddingBottom: 2,
    color: "#444"
  },
  gridContainer: {
    flexDirection: "row",
    justifyContent: "space-between"
  },
  gridColumn: {
    width: "48%"
  },
  fieldGroup: {
    marginBottom: 8
  },
  label: {
    fontSize: 9,
    color: "#888888",
    marginBottom: 2,
    fontFamily: "Helvetica-Bold"
  },
  value: {
    fontSize: 10,
    color: "#000000"
  },
  listContainer: {
    marginLeft: 10
  },
  listItem: {
    fontSize: 10,
    marginBottom: 3,
    color: "#333",
    lineHeight: 1.4
  },
  symptomRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 3,
  },
  symptomGlyph: {
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    width: 12,
  },
  symptomName: {
    fontSize: 10,
    color: "#333",
    lineHeight: 1.4
  },
  paragraphText: {
    fontSize: 10,
    color: "#333",
    lineHeight: 1.5,
    textAlign: "justify"
  },
  table: {
    width: "100%",
    marginTop: 5
  },
  tableHeader: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#dddddd",
    paddingVertical: 6,
    paddingHorizontal: 4,
    backgroundColor: "#f8f9fa"
  },
  tableHeaderCell: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: "#444444",
    textAlign: "left",
    paddingRight: 4
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 8,
    paddingHorizontal: 4,
    borderBottomWidth: 0.5,
    borderBottomColor: "#eeeeee"
  },
  tableRowEven: {
    backgroundColor: "#ffffff"
  },
  tableRowOdd: {
    backgroundColor: "#f9fbfd"
  },
  tableCell: {
    fontSize: 9,
    color: "#333333",
    textAlign: "left",
    paddingRight: 10
  },
  emptyText: {
    color: "#999999",
    fontSize: 10,
    marginLeft: 11,
    fontStyle: "italic"
  },
  blockItem: {
    marginBottom: 15,
    paddingBottom: 10,
    borderBottomWidth: 0.5,
    borderBottomColor: '#eee'
  },
  blockTitle: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    color: '#4a90e2',
    marginBottom: 5
  },
  attestationBox: {
    marginTop: 30,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#000'
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 40,
    right: 40,
    borderTopWidth: 0.5,
    borderTopColor: '#cccccc',
    paddingTop: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 8,
    color: '#888888'
  }
});

// Safe string conversion - ensures we never pass undefined/null/objects to Text
const safeStr = (val) => {
  if (val === null || val === undefined) return "--";
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
};

// AI-generated care plan text marks emphasis with literal "**double
// asterisks**" (see src/utils/parseBoldMarkers.js, shared with the HTML
// care-plan views) - react-pdf supports nested <Text style=...> children
// for inline mixed-style runs, so bold segments render as their own
// nested Text node in a muted grey instead of showing the raw markers.
const renderBoldTextPdf = (val) =>
  parseBoldSegments(safeStr(val)).map((segment, i) =>
    segment.bold ? (
      <Text key={i} style={{ fontFamily: "Helvetica-Bold", color: "#6b7280" }}>
        {segment.text}
      </Text>
    ) : (
      segment.text
    )
  );

// Helper to format seconds to time (HH:MM:SS or MM:SS)
const formatSecondsToTime = (totalSeconds) => {
  if (!totalSeconds || totalSeconds <= 0) return "--";
  try {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  } catch {
    return "--";
  }
};

// Safe array helper
const safeArray = (arr) => {
  if (!arr) return [];
  if (!Array.isArray(arr)) return [];
  return arr;
};

const CarePlanAuditReportPDF = ({
  patient,
  carePlanData,
  conditions,
  medications,
  notes,
  reportDate,
  logo,
  billingData,
  lastEditedDate,
  finalizedDate,
  finalizedBy,
  monthlyMonitoringTime,
  monitoringSessions
}) => {
  const patientName = `${safeStr(patient?.user?.first_name)} ${safeStr(patient?.user?.last_name)}`.trim() || "--";

  const generationTime = new Date().toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  });

  const icdCodes = safeArray(billingData?.icd_codes).length > 0
    ? billingData.icd_codes.join(", ")
    : safeArray(conditions).map(c => c?.icd_code).filter(Boolean).join(", ") || "--";

  // Safe list renderer - handles strings, arrays, and other types.
  // SCRUM-941: `numbered` lists (Treatment Goals, Planned Interventions)
  // show "1." markers instead of bullets.
  const renderList = (text, numbered = false) => {
    if (!text) return <Text style={styles.listItem}>--</Text>;
    const marker = (i) => (numbered ? `${i + 1}.` : "•");

    try {
      // Handle arrays
      if (Array.isArray(text)) {
        if (text.length === 0) return <Text style={styles.listItem}>--</Text>;
        return text.map((item, i) => (
          <Text key={i} style={styles.listItem}>{marker(i)} {renderBoldTextPdf(item)}</Text>
        ));
      }
      // Handle strings
      if (typeof text === 'string') {
        const items = text.split("\n").filter(s => s.trim());
        if (items.length === 0) return <Text style={styles.listItem}>--</Text>;
        return items.map((item, i) => (
          <Text key={i} style={styles.listItem}>{marker(i)} {renderBoldTextPdf(item)}</Text>
        ));
      }
      // Fallback
      return <Text style={styles.listItem}>• {renderBoldTextPdf(text)}</Text>;
    } catch {
      return <Text style={styles.listItem}>--</Text>;
    }
  };

  // SCRUM-738: Symptoms carry a tri-state (yes/no/unset) per line - rendered
  // as a check/X glyph instead of renderList's plain bullet, same two states
  // as the editor's control (SCRUM-755).
  const SYMPTOM_GLYPH = {
    [SYMPTOM_STATES.YES]: { glyph: "✓", color: "#10b981" },
    [SYMPTOM_STATES.NO]: { glyph: "X", color: "#ef4444" },
  };

  const renderSymptoms = (text) => {
    if (!text) return <Text style={styles.listItem}>--</Text>;
    try {
      const symptoms = parseSymptomsString(typeof text === "string" ? text : "")
        .filter(s => s.name.trim());
      if (symptoms.length === 0) return <Text style={styles.listItem}>--</Text>;
      return symptoms.map((symptom, i) => {
        const { glyph, color } = SYMPTOM_GLYPH[symptom.state];
        return (
          <View key={i} style={styles.symptomRow}>
            <Text style={[styles.symptomGlyph, { color }]}>{glyph}</Text>
            <Text style={styles.symptomName}>{symptom.name}</Text>
          </View>
        );
      });
    } catch {
      return <Text style={styles.listItem}>--</Text>;
    }
  };

  // Safe item list renderer for simple arrays. SCRUM-941: `numbered` lists
  // (Potential Barriers, Recommendations) skip blank rows and show "1." markers.
  const renderSimpleList = (arr, numbered = false) => {
    const safeArr = numbered
      ? safeArray(arr).filter((item) => (item ?? "").toString().trim())
      : safeArray(arr);
    if (safeArr.length === 0) return <Text style={styles.emptyText}>None</Text>;
    return safeArr.map((item, i) => (
      <Text key={i} style={styles.listItem}>{numbered ? `${i + 1}.` : "•"} {renderBoldTextPdf(item)}</Text>
    ));
  };

  // Safe access helpers for Behavioral Data
  const getBehavioralVal = (key1, key2) => {
    const val = carePlanData?.behavioral_data?.[key1] || carePlanData?.behavioral_data?.[key2];
    return safeStr(val);
  };

  // Filter for CCM Notes only
  const ccmNotes = safeArray(notes).filter(n => n?.note_type === 'ccm_note');
  const safeConditions = safeArray(conditions);
  const safeMedications = safeArray(medications);
  const safeSessions = safeArray(monitoringSessions);
  const safeCcmBillings = safeArray(billingData?.ccm_billings);

  return (
    <Document>
      <Page size="A4" style={styles.page}>

        {/* Header */}
        <View style={styles.header}>
          {logo && <Image src={logo} style={styles.logo} />}
          <View style={styles.headerSeparator} />
          <Text style={styles.locationName}>{safeStr(patient?.location?.name) || "Dialysis Center"}</Text>
          <View style={styles.reportTitleRow}>
             <Text style={styles.reportTitle}>CCM Audit Report</Text>
             <Text style={styles.reportDate}>{safeStr(reportDate)}</Text>
          </View>
          <Text style={styles.lastEdited}>Last Edited: {safeStr(lastEditedDate)}</Text>
        </View>

        {/* General Information */}
        <View style={styles.section}>
          <View style={styles.sectionTitleWrapper}>
            <View style={styles.blueBar} />
            <Text style={styles.sectionTitle}>General Information</Text>
          </View>
          <View style={styles.gridContainer}>
            <View style={styles.gridColumn}>
              <View style={styles.fieldGroup}><Text style={styles.label}>Patient Name</Text><Text style={styles.value}>{patientName}</Text></View>
              <View style={styles.fieldGroup}><Text style={styles.label}>Birthday</Text><Text style={styles.value}>{formatUSADate(patient?.user?.dob)}</Text></View>
              <View style={styles.fieldGroup}><Text style={styles.label}>Email</Text><Text style={styles.value}>{safeStr(patient?.user?.email)}</Text></View>
            </View>
            <View style={styles.gridColumn}>
              <View style={styles.fieldGroup}><Text style={styles.label}>Phone</Text><Text style={styles.value}>{safeStr(patient?.user?.primary_phone)}</Text></View>
              <View style={styles.fieldGroup}><Text style={styles.label}>Complex</Text><Text style={styles.value}>{carePlanData?.complex ? "Yes" : "No"}</Text></View>
              <View style={styles.fieldGroup}><Text style={styles.label}>Monitoring Time</Text><Text style={styles.value}>{safeStr(monthlyMonitoringTime)}</Text></View>
              <View style={styles.fieldGroup}><Text style={styles.label}>ICD-10</Text><Text style={styles.value}>{icdCodes}</Text></View>
            </View>
          </View>
        </View>

        {/* Care Manager Metadata */}
        <View style={styles.section}>
          <View style={styles.sectionTitleWrapper}>
            <View style={styles.blueBar} />
            <Text style={styles.sectionTitle}>Care Manager Metadata</Text>
          </View>
          <View style={styles.gridContainer}>
            <View style={styles.gridColumn}>
              <View style={styles.fieldGroup}>
                <Text style={styles.label}>Care Manager</Text>
                <Text style={styles.value}>
                  {patient?.case_manager ? `${safeStr(patient.case_manager.first_name)} ${safeStr(patient.case_manager.last_name)}`.trim() || "--" : "--"}
                </Text>
              </View>
              <View style={styles.fieldGroup}>
                <Text style={styles.label}>Finalized By</Text>
                <Text style={styles.value}>{safeStr(finalizedBy)}</Text>
              </View>
            </View>
            <View style={styles.gridColumn}>
              <View style={styles.fieldGroup}>
                <Text style={styles.label}>Finalization Date</Text>
                <Text style={styles.value}>{safeStr(finalizedDate)}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* CPT Codes */}
        {safeCcmBillings.length > 0 && (
          <View style={styles.section}>
             <View style={styles.sectionTitleWrapper}>
                <View style={styles.blueBar} />
                <Text style={styles.sectionTitle}>CPT Codes Generated</Text>
             </View>
             <View style={styles.table}>
                <View style={styles.tableHeader}>
                   <Text style={[styles.tableHeaderCell, {width: '15%'}]}>Code</Text>
                   <Text style={[styles.tableHeaderCell, {width: '10%'}]}>Units</Text>
                   <Text style={[styles.tableHeaderCell, {width: '75%'}]}>Description</Text>
                </View>
                {safeCcmBillings.map((bill, index) => (
                   <View key={index} style={[styles.tableRow, index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd]}>
                      <Text style={[styles.tableCell, {width: '15%'}]}>{safeStr(bill?.code)}</Text>
                      <Text style={[styles.tableCell, {width: '10%'}]}>{safeStr(bill?.units)}</Text>
                      <Text style={[styles.tableCell, {width: '75%'}]}>{safeStr(bill?.description)}</Text>
                   </View>
                ))}
             </View>
          </View>
        )}

        {/* Health Summary */}
        <View style={styles.section}>
          <View style={styles.sectionTitleWrapper}>
            <View style={styles.blueBar} />
            <Text style={styles.sectionTitle}>Health Summary</Text>
          </View>

          <Text style={styles.subSectionTitle}>Physiological Averages</Text>
          <View style={styles.gridContainer}>
             <View style={styles.gridColumn}>
                <View style={styles.fieldGroup}><Text style={styles.label}>Blood Pressure</Text><Text style={styles.value}>{safeStr(carePlanData?.physiological_data?.blood_pressure)} mm Hg</Text></View>
                <View style={styles.fieldGroup}><Text style={styles.label}>Weight</Text><Text style={styles.value}>{safeStr(carePlanData?.physiological_data?.weight)} lbs</Text></View>
                <View style={styles.fieldGroup}><Text style={styles.label}>Blood Glucose</Text><Text style={styles.value}>{safeStr(carePlanData?.physiological_data?.blood_glucose)} mg/dL</Text></View>
                <View style={styles.fieldGroup}><Text style={styles.label}>BMI</Text><Text style={styles.value}>{carePlanData?.physiological_data?.bmi ? `${carePlanData.physiological_data.bmi} kg/m2` : "--"}</Text></View>
             </View>
             <View style={styles.gridColumn}>
                <View style={styles.fieldGroup}><Text style={styles.label}>Height</Text><Text style={styles.value}>{safeStr(carePlanData?.physiological_data?.height)} {getHeightUnit(carePlanData?.created_at)}</Text></View>
                <View style={styles.fieldGroup}><Text style={styles.label}>DOB Confirmed</Text><Text style={styles.value}>{carePlanData?.dob_confirmed ? "Yes" : "No"}</Text></View>
                <View style={styles.fieldGroup}><Text style={styles.label}>Consent for Monthly Calls</Text><Text style={styles.value}>{carePlanData?.consent_for_calls ? "Yes" : "No"}</Text></View>
             </View>
          </View>

          <Text style={styles.subSectionTitle}>Behavioral Averages</Text>
          <View style={styles.gridContainer}>
             <View style={styles.gridColumn}>
                <View style={styles.fieldGroup}><Text style={styles.label}>Sleep</Text><Text style={styles.value}>{getBehavioralVal('sleep_hours', 'sleep')}</Text></View>
                <View style={styles.fieldGroup}><Text style={styles.label}>Exercise</Text><Text style={styles.value}>{getBehavioralVal('exercise_hours_per_week', 'exercise')}</Text></View>
             </View>
             <View style={styles.gridColumn}>
                <View style={styles.fieldGroup}><Text style={styles.label}>Diet/Water</Text><Text style={styles.value}>{getBehavioralVal('water_diet', 'diet_per_day')}</Text></View>
             </View>
          </View>
        </View>

        {/* Problem List */}
        <View style={styles.section}>
          <View style={styles.sectionTitleWrapper}>
             <View style={styles.blueBar} />
             <Text style={styles.sectionTitle}>Problem List & Education</Text>
          </View>

          <View style={{marginBottom: 10}}>
              <Text style={styles.subSectionTitle}>PMHx</Text>
              <View style={styles.listContainer}>
                {renderSimpleList(carePlanData?.pmhx)}
              </View>
          </View>

          <View style={{marginBottom: 10}}>
              <Text style={styles.subSectionTitle}>Potential Barriers</Text>
              <View style={styles.listContainer}>
                {renderSimpleList(carePlanData?.potential_barriers, true)}
              </View>
          </View>

          <View style={{marginBottom: 10}}>
              <Text style={styles.subSectionTitle}>Recent Hospitalization</Text>
              <View style={styles.listContainer}>
                {renderSimpleList(carePlanData?.recent_hospitalization)}
              </View>
          </View>

          <View style={{marginBottom: 10}}>
              <Text style={styles.subSectionTitle}>Recommendations</Text>
              <View style={styles.listContainer}>
                {renderSimpleList(carePlanData?.recommendations, true)}
              </View>
          </View>
        </View>

        {/* Medication Reconciliation */}
        <View style={styles.section}>
          <View style={styles.sectionTitleWrapper}>
            <View style={styles.blueBar} />
            <Text style={styles.sectionTitle}>Medication Reconciliation</Text>
          </View>

          <View style={{marginBottom: 10}}>
            <Text style={styles.subSectionTitle}>Medications</Text>
            <View style={styles.listContainer}>
              {safeMedications.length > 0 ? safeMedications.map((med, index) => (
                <Text key={index} style={styles.listItem}>• {safeStr(med?.name)}</Text>
              )) : <Text style={styles.emptyText}>No medications recorded.</Text>}
            </View>
          </View>

          <View style={styles.gridContainer}>
             <View style={styles.gridColumn}>
                <Text style={styles.subSectionTitle}>Allergies</Text>
                {renderSimpleList(carePlanData?.allergies)}
             </View>
             <View style={styles.gridColumn}>
                <Text style={styles.subSectionTitle}>Reported Side Effects</Text>
                {renderSimpleList(carePlanData?.reported_side_effect)}
             </View>
          </View>
        </View>

        {/* Functional Assessment */}
        <View style={styles.section}>
          <View style={styles.sectionTitleWrapper}>
             <View style={styles.blueBar} />
             <Text style={styles.sectionTitle}>Functional Assessment</Text>
          </View>
          <View style={{marginBottom: 10}}>
              <Text style={styles.subSectionTitle}>Activities of Daily Living (ADLs)</Text>
              <View style={styles.listContainer}>
                {renderSimpleList(carePlanData?.activities_of_daily_living)}
              </View>
          </View>
          <View style={{marginBottom: 10}}>
              <Text style={styles.subSectionTitle}>Caregiver Assessment</Text>
              <View style={styles.listContainer}>
                {renderSimpleList(carePlanData?.caregiver_assessment)}
              </View>
          </View>
        </View>

        {/* Chronic Conditions */}
        <View style={styles.section}>
          <View style={styles.sectionTitleWrapper}>
             <View style={styles.blueBar} />
             <Text style={styles.sectionTitle}>Chronic Conditions</Text>
          </View>
          {safeConditions.length > 0 ? safeConditions.map((cond, index) => (
             <View key={index} style={styles.blockItem}>
                <Text style={styles.blockTitle}>Condition #{index + 1}: {safeStr(cond?.name)}</Text>
                <View style={{marginBottom: 5}}>
                   <Text style={styles.label}>Prognosis:</Text>
                   {renderList(cond?.prognosis)}
                </View>
                <View style={{marginBottom: 5}}>
                   <Text style={styles.label}>Symptoms:</Text>
                   {wasCreatedOnOrAfterSymptomsCutoff(carePlanData?.created_at)
                     ? renderSymptoms(cond?.symptoms)
                     : renderList(cond?.symptoms)}
                </View>
                <View style={{marginBottom: 5}}>
                   <Text style={styles.label}>Treatment Goals:</Text>
                   {renderList(cond?.treatment_goals, true)}
                </View>
                <View style={{marginBottom: 5}}>
                   <Text style={styles.label}>Planned Interventions:</Text>
                   {renderList(cond?.planned_interventions, true)}
                </View>
                <Text style={{fontSize: 9, fontFamily: "Helvetica-Bold"}}>ICD-10 Code: {safeStr(cond?.icd_code)}</Text>
             </View>
          )) : <Text style={styles.emptyText}>No chronic conditions recorded.</Text>}
        </View>

        {/* Careplan Notes */}
        {carePlanData?.note && (
           <View style={styles.section}>
              <View style={styles.sectionTitleWrapper}>
                 <View style={styles.blueBar} />
                 <Text style={styles.sectionTitle}>Careplan Notes</Text>
              </View>
              <View style={{padding: 5}}>
                 <Text style={styles.paragraphText}>{renderBoldTextPdf(carePlanData.note)}</Text>
              </View>
           </View>
        )}

        {/* CCM Notes */}
        <View style={styles.section}>
          <View style={styles.sectionTitleWrapper}>
             <View style={styles.blueBar} />
             <Text style={styles.sectionTitle}>CCM Notes</Text>
          </View>
          <View style={styles.table}>
             <View style={styles.tableHeader}>
                <Text style={[styles.tableHeaderCell, {width: '20%'}]}>Date</Text>
                <Text style={[styles.tableHeaderCell, {width: '60%'}]}>Note</Text>
                <Text style={[styles.tableHeaderCell, {width: '20%'}]}>Creator</Text>
             </View>
             {ccmNotes.length > 0 ? ccmNotes.map((note, index) => (
                <View key={index} style={[styles.tableRow, index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd]}>
                   <Text style={[styles.tableCell, {width: '20%'}]}>{formatUSADateTime(note?.created_at)}</Text>
                   <Text style={[styles.tableCell, {width: '60%'}]}>{safeStr(note?.note_text)}</Text>
                   <Text style={[styles.tableCell, {width: '20%'}]}>{safeStr(note?.assigned_by_name)}</Text>
                </View>
             )) : <Text style={styles.emptyText}>No CCM notes available.</Text>}
          </View>
        </View>

        {/* Monitoring Sessions */}
        <View style={styles.section}>
          <View style={styles.sectionTitleWrapper}>
             <View style={styles.blueBar} />
             <Text style={styles.sectionTitle}>Monitoring Sessions</Text>
          </View>
          <View style={styles.table}>
             <View style={styles.tableHeader}>
                <Text style={[styles.tableHeaderCell, {width: '30%'}]}>Date</Text>
                <Text style={[styles.tableHeaderCell, {width: '25%'}]}>Duration</Text>
                <Text style={[styles.tableHeaderCell, {width: '45%'}]}>Staff</Text>
             </View>
             {safeSessions.length > 0 ? safeSessions.map((session, index) => (
                <View key={index} style={[styles.tableRow, index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd]}>
                   <Text style={[styles.tableCell, {width: '30%'}]}>{formatNaiveDateTime(session?.created_at)}</Text>
                   <Text style={[styles.tableCell, {width: '25%'}]}>{formatSecondsToTime(session?.total_ccm_seconds)}</Text>
                   <Text style={[styles.tableCell, {width: '45%'}]}>{safeStr(session?.staff_member?.full_name || session?.assigned_by_name)}</Text>
                </View>
             )) : <Text style={styles.emptyText}>No monitoring sessions recorded.</Text>}
          </View>
        </View>

        {/* Attestation */}
        <View style={styles.attestationBox}>
           <Text style={{fontSize: 9, fontStyle: 'italic', marginBottom: 10}}>
              I attest that the Chronic Care Management services documented in this report were provided under my supervision and reflect accurate and honest clinical care delivered during the specified time period.
           </Text>
           <View style={{flexDirection: 'row', justifyContent: 'space-between'}}>
              <Text style={{fontSize: 9, fontFamily: "Helvetica-Bold"}}>Electronically Signed By: {safeStr(finalizedBy)}</Text>
              <Text style={{fontSize: 9}}>Date: {safeStr(finalizedDate)}</Text>
           </View>
        </View>

        {/* Watermark */}
        {logo && <Image src={logo} style={styles.watermark} fixed />}

        <View style={styles.footer} fixed>
          <Text>{patientName} - CCM Audit Report</Text>
          <Text render={({ pageNumber, totalPages }) => `${pageNumber} of ${totalPages}`} />
          <Text>{generationTime}</Text>
        </View>
      </Page>
    </Document>
  );
};

export default CarePlanAuditReportPDF;
